import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import _uniq from "lodash/uniq";

import ShareInformationTopSection from "components/lib/Global/ShareInformationTopSection";
import NominalSharesTable from "components/PLCTABS/NominalShareCapitalTab/NominalSharesTable";
import SharesCategoryTabs from "components/PLCTABS/NominalShareCapitalTab/SharesCategoryTabs";
import CommentsList from "components/lib/Global/CommentsList";
import { addComment, editComment } from "state/slices/comment";
import { getTakenShares, roundNumber } from "utils";
import ProposedSharesTable from "components/PLCTABS/NominalShareCapitalTab/ProposedSharesTable";
import AllotmentResultsTable from "components/CompanyDashboard/MaintainYourCompany/Changes/ShareCapital/AllotmentOfShares/AllotmentResultsTable";

export default function ShareInformation({ disabledRightsEditor, isViewMode, transactions }) {
  const dispatch = useDispatch();
  const { company } = useSelector((state) => state.companySlice);
  const { comments } = useSelector((state) => state.commentsSlice);
  const { shares } = useSelector((state) => state).sharesTableSlice;
  const { transactionUnderReview } = useSelector(
    (state) => state.transactionSlice
  );
  const { authorizations, allotments, shareCapitalChanges, } =
    transactionUnderReview.items;
  const shareInformationComments = comments.filter(
    (comment) => comment.formName === "shareInformation"
  );
  const companyId = transactions[0].companyId;

  const takenShares = getTakenShares(
    useSelector((state) => state.companyOfficialsSlice.companyOfficials.items)
  );

  const sumNominalCapital = shares
    .map((share) => share.sharesValue * share.sharesNumber)
    .reduce((a, b) => a + b, 0);

  const sumOfVotingRights = shares.reduce(
    (acc, share) => roundNumber(acc + Number(share.votingRights)),
    0
  );

  let changeType = "authorization";
  let allotmentMode = "";

  if (shareCapitalChanges?.length > 0) {
    changeType = shareCapitalChanges[0]?.changeType;
    const rightsIssue = "rightsIssue";
    const proRata = "proRata";
    const manual = "manual";

    const hasRightsIssue = shareCapitalChanges?.some(
      (c) => c.option === rightsIssue
    );
    const hasProRata = shareCapitalChanges?.some((c) => c.option === proRata);
    const hasManual = shareCapitalChanges?.some(
      (c) => c.option === manual || c.option === "specificShareholderOffer"
    );

    if (hasRightsIssue) {
      allotmentMode = rightsIssue;
    } else if (hasProRata) {
      allotmentMode = proRata;
    } else if (hasManual) {
      allotmentMode = manual;
    }
  }

  const handleSaveComment = async (data) => {
    if (data?.commentId) {
      await dispatch(editComment({ commentId: data.commentId, data }));
    } else {
      await dispatch(
        addComment({
          data: {
            ...data,
            company: company._id,
          },
        })
      );
    }
  };

  const getSubmittedAllocations = (mode) => {
    return shareCapitalChanges
      .filter((change) => change.option === mode)
      .map((change) => ({
        classOfShares: change.classOfShares,
        sharesOnOffer: change.sharesOnOffer,
        AmountCreditedAsPaidUpCapitalPerShare:
          change.AmountCreditedAsPaidUpCapitalPerShare,
        allocations: change.allocations || [],
        remainder: change.remainder || 0,
        option: change.option,
        forEvery: change.forEvery,
        allotAmount: change.allotAmount,
        distributionTargetClasses: change.distributionTargetClasses || [],
      }));
  };

  const getTransactionAllotmentData = () => {
    // Extract allotment data from shareCapitalChanges
    if (shareCapitalChanges?.length > 0) {
      return shareCapitalChanges.map((change) => ({
        classOfShares: change.classOfShares,
        sharesOnOffer: change.sharesOnOffer,
        offerPricePerShare: change.offerPricePerShare,
        premiumPerShare: change.premiumPerShare,
        dateOfOffer: change.dateOfOffer,
        AuthorizationId: change.AuthorizationId,
        nominalShareId: change.nominalShareId,
        _id: change._id,
      }));
    }
    return [];
  };

  const allocations = getSubmittedAllocations(allotmentMode);

  return (
    <div className="mt-6">
      {shareInformationComments.length > 0 && (
        <div className="px-8 pt-5 pb-6 mb-4 bg-white">
          <CommentsList
            comments={shareInformationComments}
            toggleCommentStatus={!isViewMode}
          />
          <p className="mt-6 text-sm text-primary">
            Comments: {shareInformationComments.length} of{" "}
            {shareInformationComments.length}
          </p>
        </div>
      )}
      <ShareInformationTopSection
        sumNominalCapital={sumNominalCapital}
        sumOfVotingRights={sumOfVotingRights}
      />
      <NominalSharesTable
        handleEdit={() => null}
        takenShares={takenShares}
        handleView={() => null}
      />
      <ProposedSharesTable
        authorizations={authorizations}
        showAddCommentColumn={!isViewMode}
        handleSaveComment={handleSaveComment}
        comments={shareInformationComments}
        changeType={changeType}
      />
      {allocations && allocations?.length > 0 && (
        <AllotmentResultsTable
          allocations={allocations}
          mode={allotmentMode}
          isEditable={true}
          isInReview={true}
          availableShares={(() => {
            // Get available shares for current process type
            return getTransactionAllotmentData() || [];
          })()}
          companyId={companyId}
        />
      )}

      <div className="mt-12">
        <SharesCategoryTabs
          disabledRightsEditor={disabledRightsEditor}
          nominalShares={shares}
        />
      </div>
    </div>
  );
}
